
/**
 * Sistema de registro de errores centralizado
 * 
 * Este módulo proporciona funciones para manejar y registrar errores de manera consistente.
 * En un entorno de producción, se podría conectar a un servicio de seguimiento de errores.
 */

type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

interface ErrorDetails {
  message: string;
  code?: string;
  severity?: ErrorSeverity;
  context?: Record<string, any>;
}

// Esta función se podría mejorar para enviar errores a un servicio externo en producción
export const logError = (error: Error | string, details?: Partial<ErrorDetails>): void => {
  const errorObj = typeof error === 'string' ? new Error(error) : error;
  
  const errorLog = {
    timestamp: new Date().toISOString(),
    message: errorObj.message,
    stack: errorObj.stack,
    ...details
  };
  
  // En desarrollo, registramos en la consola
  console.error('❌ Error registrado:', errorLog);
  
  // En producción, podríamos enviar a un servicio como Sentry, LogRocket, etc.
  if (import.meta.env.PROD) {
    // Aquí iría el código para enviar a un servicio externo
    // sendToErrorTrackingService(errorLog);
  }
};

// Esta función se usaría para errores que no deberían interrumpir el flujo de la aplicación
export const logWarning = (message: string, context?: Record<string, any>): void => {
  const warningLog = {
    timestamp: new Date().toISOString(),
    message,
    context
  };
  
  console.warn('⚠️ Advertencia:', warningLog);
};

// Helper para manejar errores en llamadas API
export const handleApiError = (error: unknown, fallbackMessage = 'Error en la petición'): string => {
  let errorMessage = fallbackMessage;
  
  if (error instanceof Error) {
    errorMessage = error.message;
    logError(error, { severity: 'medium', context: { source: 'api' } });
  } else if (typeof error === 'string') {
    errorMessage = error;
    logError(error, { severity: 'medium', context: { source: 'api' } });
  } else {
    logError(new Error(fallbackMessage), { 
      severity: 'medium', 
      context: { source: 'api', unknownError: JSON.stringify(error) } 
    });
  }
  
  return errorMessage;
};

// Función para instalar un manejador global de errores no capturados
export const setupGlobalErrorHandlers = (): void => {
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), {
      severity: 'high',
      context: {
        source: 'window.onerror',
        fileName: event.filename,
        lineNumber: event.lineno,
        columnNumber: event.colno
      }
    });
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason) || 'Promesa rechazada sin motivo');
    
    logError(error, {
      severity: 'high',
      context: {
        source: 'unhandledRejection'
      }
    });
  });
  
  console.log('✅ Manejadores globales de errores configurados');
};

// New helper for form submission errors
export const handleFormSubmissionError = (
  error: unknown, 
  toast: any, 
  errorTitle = 'Form Submission Error', 
  errorMessage = 'There was a problem submitting the form. Please try again.'
): void => {
  // Log the error first
  let detailedMessage = errorMessage;
  
  if (error instanceof Error) {
    detailedMessage = error.message;
    logError(error, { 
      severity: 'medium', 
      context: { source: 'formSubmission' } 
    });
  } else {
    logError(new Error(errorMessage), { 
      severity: 'medium', 
      context: { 
        source: 'formSubmission', 
        rawError: typeof error === 'object' ? JSON.stringify(error) : String(error) 
      } 
    });
  }
  
  // Show user-friendly toast
  if (toast) {
    toast({
      title: errorTitle,
      description: errorMessage,
      variant: 'destructive'
    });
  }
  
  // Return without throwing to ensure the app doesn't crash
  return;
};

// Function to check network status 
export const isOnline = (): boolean => {
  return navigator.onLine;
};

// Utility to handle offline state in form submissions
export const handleOfflineSubmission = (toast: any): boolean => {
  if (!isOnline()) {
    toast({
      title: "You're offline",
      description: "Please check your internet connection and try again.",
      variant: "destructive"
    });
    return true; // Submission should be stopped
  }
  return false; // Can proceed with submission
};
